/**
 * Cuándo deja de esperarse la confirmación de un cambio de plan.
 *
 * ## Por qué es una función y no un `if` dentro de la pantalla
 *
 * Era un `if`, y estaba mal. La espera terminaba cuando la suscripción estaba
 * **viva**, que es la señal correcta al contratar por primera vez —no había
 * suscripción y pasa a haberla— pero no al **subir de plan**: ahí la
 * suscripción ya estaba viva antes de empezar, así que el aviso «estamos
 * confirmando tu pago» se apagaba en el mismo instante en que se encendía y no
 * llegaba a verse nunca.
 *
 * Ocurrió el 2026-10-06 con el cobro funcionando por detrás: Stripe cobró la
 * diferencia, el webhook subió el plan, y la pantalla no contó nada. El fallo
 * no se vio en ninguna prueba porque vivía dentro de un componente.
 *
 * ## Las dos señales
 *
 * ```text
 * SUSCRIPCION   contratar: hay una suscripción viva
 * PLAN          subir: el servidor deja de ofrecer ese plan como posible,
 *               que es su forma de decir «ya lo tienes»
 * ```
 *
 * La segunda se apoya en la lista que manda el servidor a propósito: esta
 * aplicación no sabe qué plan está por encima de cuál (`URS §105`), así que no
 * puede comparar el plan actual con el pedido. Lo que sí puede es ver que ya no
 * se lo ofrecen.
 */
import type { PlanDePago } from "../api/cliente";

export type Espera = { readonly hasta: "SUSCRIPCION" } | { readonly hasta: "PLAN"; readonly objetivo: PlanDePago };

/** Lo que la pantalla sabe en este momento. `null` = todavía no ha cargado. */
export type Visto = { readonly viva: boolean; readonly upgrades: readonly PlanDePago[] };

export function esperaCumplida(espera: Espera | null, visto: Visto | null): boolean {
  if (espera === null) return false;
  /*
    Sin datos no se decide NADA. Con la pantalla cargando, `upgrades` está
    vacía, y una lista vacía se parece exactamente a «ya lo tienes»: daría la
    espera por cumplida antes de haber preguntado una sola vez.
  */
  if (visto === null) return false;
  return espera.hasta === "SUSCRIPCION" ? visto.viva : !visto.upgrades.includes(espera.objetivo);
}
